import * as tokens from '@utmost.co/utmost-design-tokens';
import { Components } from '../../../styles';
import { DEFAULT_BUTTON_INPUT_ELEVATION } from './commons';
import { labelTheme } from './label';
import { OverridesStyleRules } from '@mui/material/styles/overrides';
import { tokenToPx } from './getToken';

const isTest = process.env.NODE_ENV === 'test';

const INPUT_FONT_FAMILY = `var(--input-font-family, Arial), sans-serif`;
const INPUT_SIZE = 'var(--input-font-size-md, 13.3px)';
const INPUT_MIN_HEIGHT = 'var(--input-min-height-md, 40px)';

export const inputBaseRootStyle = {
  borderRadius: 4,
  position: 'relative',
  backgroundColor: tokens.colorSurface,
  border: '1px solid',
  borderColor: tokens.colorSurfaceVariant,
  color: `${tokens.colorOnBackground} !important`,
  minHeight: INPUT_MIN_HEIGHT,
  padding: `0 ${tokenToPx('spacingXxs')}`,
  marginTop: 0,
  fontSize: INPUT_SIZE,
  fontFamily: INPUT_FONT_FAMILY,
  transition: 'all 0.25s ease-in-out',
  '&:hover': {
    ...DEFAULT_BUTTON_INPUT_ELEVATION,
    borderColor: tokens.colorOutline
  },
  '&:focus-within': {
    borderColor: tokens.colorPrimary
  },
  // latest jsdom > nwsapi (used as css selector engine) doesnt work with this selector
  [isTest ? '&.ignored-rule' : '&:has(.MuiInputBase-input:-webkit-autofill)']: {
    backgroundColor: tokens.paletteBlue95
  },
  '&.Mui-disabled': {
    pointerEvents: 'none',
    borderStyle: 'dotted',
    background: 'transparent',
    color: 'rgba(0, 0, 0, 0.38) !important'
  },
  '&.Mui-readOnly': {
    pointerEvents: 'none',
    borderStyle: 'dotted',
    background: 'transparent',
    color: 'rgba(0, 0, 0, 0.38) !important'
  },
  '& .MuiInputBase-input::placeholder': {
    color: `${tokens.paletteNeutral50} !important`,
    opacity: `1 !important`,
    fontSize: `${INPUT_SIZE} !important`
  }
} satisfies OverridesStyleRules;

export const inputTheme = {
  MuiTextField: {
    styleOverrides: {
      root: {
        fontSize: 14,
        '.MuiFormLabel-root + .MuiInputBase-root': {
          marginTop: 8
        },
        '&.no-error-text .Mui-error': {
          borderColor: tokens.colorAlert
        },
        '&.date-picker': {
          '.MuiFormLabel-root + .MuiInputBase-root': {
            marginTop: 6
          },
          '.MuiInputAdornment-positionEnd .MuiButtonBase-root': {
            marginRight: -4,
            color: tokens.colorOnSurface
          },
          '.Mui-disabled .MuiInputAdornment-positionEnd .MuiButtonBase-root': {
            color: tokens.stateOnSurfaceDisabledContentStateLayerColor
          }
        },
        variants: [
          {
            props: { variant: 'standard' },
            style: {
              '.MuiFormLabel-root': labelTheme.MuiFormLabel.styleOverrides.root,
              'label + &': {
                marginTop: 4
              },
              '& .MuiInputBase-root': inputBaseRootStyle,
              '& .MuiInputBase-input': {
                // it is better to keep fonts aligned with Chrome's autocomplete font style - it avoids font flickering
                fontSize: INPUT_SIZE,
                fontFamily: INPUT_FONT_FAMILY,
                padding: '4px 4px 5px 4px',
                // latest jsdom > nwsapi (used as css selector engine) doesnt work with this selector
                [isTest
                  ? '&.ignored-rule'
                  : '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active']:
                  {
                    WebkitBackgroundClip: 'text'
                  }
              },
              '&.time-picker .MuiInputAdornment-positionEnd button': {
                marginRight: -7
              },
              '&.date-time-picker .MuiInputAdornment-positionEnd button': {
                marginRight: -4
              }
            }
          },
          {
            props: { variant: 'filled' },
            style: {
              '.MuiFormLabel-root': labelTheme.MuiFormLabel.styleOverrides.root,
              'label + &': {
                marginTop: 4
              },
              '& .MuiInputBase-root': {
                ...inputBaseRootStyle,
                backgroundColor: tokens.colorSurfaceVariant
              },
              '& .MuiInputBase-input': {
                // it is better to keep fonts aligned with Chrome's autocomplete font style - it avoids font flickering
                fontSize: INPUT_SIZE,
                fontFamily: INPUT_FONT_FAMILY,
                padding: '4px 4px 5px 4px',
                // latest jsdom > nwsapi (used as css selector engine) doesnt work with this selector
                [isTest
                  ? '&.ignored-rule'
                  : '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active']:
                  {
                    WebkitBackgroundClip: 'text'
                  }
              },

              '& .MuiInputBase-input::placeholder': {
                color: `${tokens.colorOnSurfaceVariant} !important`,
                opacity: `1 !important`
              }
            }
          },
          {
            props: { multiline: true },
            style: {
              '& .MuiInputBase-root': {
                height: 'auto'
              }
            }
          }
        ]
      }
    },
    defaultProps: {
      variant: 'standard'
    }
  }
} satisfies Components;
